import { device } from '@seek/ca-myprofile-utils';
import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Box, Stack } from 'braid-design-system';
import _isEmpty from 'lodash/isEmpty';
import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useFormContext } from 'react-hook-form';
import { useEffectOnce } from 'react-use';

import type { Resume } from 'src/__generated__/graphql';
import {
  CoverLetterFields,
  type CoverLetterFieldsValues,
} from 'src/components/CoverLetterFields/CoverLetterFields';
import PersonalDetails, { ErrorKey } from 'src/components/PersonalDetails';
import {
  ResumeFields,
  type ResumeFieldsValues,
} from 'src/components/ResumeFields/ResumeFields';
import {
  SelectionCriteriaFields,
  type SelectionCriteriaFieldsValues,
} from 'src/components/SelectionCriteriaFields/SelectionCriteriaFields';
import { useConfig } from 'src/context/config';
import { FileAttachment } from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { tagRecording } from 'src/modules/hotjar';
import { AlertMessage } from 'src/ui/AlertMessage/AlertMessage';
import { BottomNav } from 'src/ui/BottomNav/BottomNav';
import { useSmoothScroll } from 'src/ui/ScrollOnMount/ScrollOnMount';

import translations from './.vocab';
import { PrivacyStatement } from './components/PrivacyStatement.tsx';
import { useResumeParsing } from './hooks/useResumeParsing';
import { useTracking } from './hooks/useTracking';

export interface FileWithID extends File {
  documentId?: string;
}

export type DocumentsFormValues = ResumeFieldsValues &
  CoverLetterFieldsValues &
  SelectionCriteriaFieldsValues;

interface Props {
  resumes: Resume[];
  defaultResume?: Resume;
  defaultCoverletter?: string;
  shouldRenderselectionCriteria?: boolean;
  errorMessage?: string;
  onNext?: () => void;
}

export function Documents({
  resumes,
  defaultResume,
  defaultCoverletter,
  shouldRenderselectionCriteria = false,
  errorMessage,
  onNext,
}: Props) {
  const { config } = useConfig();
  const { t } = useTranslations(translations);
  const { trackDocumentsPageView } = useTracking();
  const { requestResumeParsing } = useResumeParsing();
  const alert = useSmoothScroll();

  useEffectOnce(trackDocumentsPageView);

  const [isEditingPersonalDetails, setIsEditingPersonalDetails] =
    useState(false);
  const [unsavedPersonalDetailsWarning, setUnsavedPersonalDetailsWarning] =
    useState<ErrorKey>();
  const personalDetailsRef = useRef<HTMLDivElement>(null);

  const { formState, trigger, getValues } =
    useFormContext<DocumentsFormValues>();

  // Becoz virus scan validation is an unregistered field
  // we need to check if the form is valid using formState.isValid
  const { isValid, errors } = formState;

  const handleNext = async () => {
    // The result of trigger() is only for registered fields
    // we have to use it here becoz it will be the most up-to-date
    // validation result from formState.isValid sometimes is not updated
    const isRegisteredFieldsValid = await trigger();
    const isAllValid = isValid && isRegisteredFieldsValid && _isEmpty(errors);

    if (!isAllValid) {
      return alert.scrollIntoView();
    }

    if (isEditingPersonalDetails) {
      setUnsavedPersonalDetailsWarning(ErrorKey.UNSAVED);
      return personalDetailsRef.current?.scrollIntoView();
    }

    const resumeId = getValues('resume-select');

    if (resumeId) {
      requestResumeParsing(resumeId);
    }

    onNext?.();

    if (device.getDeviceType() === 'mobile') {
      tagRecording('mobile-apply-second-step');
    }
  };

  const validationErrors = Object.entries(formState.errors);

  const fieldNameTranslations = {
    resume: t('Resume'),
    coverLetter: t('Cover letter'),
    selectionCriteria: t('Selection criteria'),
  };

  const validationMessages = validationErrors.reduce((map, [key, error]) => {
    const prefix = key.split('-')[0] as keyof typeof fieldNameTranslations;
    const fieldName = fieldNameTranslations[prefix];
    if (!fieldName || !error) return map;
    return { ...map, [fieldName]: error.message };
  }, {});

  // to be updated with the correct path when ready
  const collectionNoticePath = useMelwaysLink('/privacy');
  const privacyPath = useMelwaysLink('/privacy');

  return (
    <>
      <Helmet>
        <title>{`${t('Choose documents')} | ${config.BRAND}`}</title>
      </Helmet>
      <alert.ScrollTarget>
        {(Boolean(errorMessage) || validationErrors.length > 0) && (
          <Box marginBottom="xxlarge">
            <AlertMessage message={errorMessage || validationMessages} />
          </Box>
        )}
      </alert.ScrollTarget>

      <Stack space="xxlarge">
        <Box ref={personalDetailsRef}>
          <PersonalDetails
            isEditing={isEditingPersonalDetails}
            onEditToggle={setIsEditingPersonalDetails}
            error={unsavedPersonalDetailsWarning}
          />
        </Box>
        <ResumeFields
          resumes={resumes}
          defaultValue={defaultResume}
          defaultMethod={resumes.length > 0 ? FileAttachment.CHANGE : undefined}
        />
        <CoverLetterFields
          defaultMethod={defaultCoverletter ? FileAttachment.CHANGE : undefined}
          defaultValue={defaultCoverletter}
        />
        {shouldRenderselectionCriteria && <SelectionCriteriaFields />}
        <PrivacyStatement
          brand={config.BRAND}
          collectionNoticeUrl={collectionNoticePath}
          privacyUrl={privacyPath}
          hasDefaultResume={Boolean(defaultResume)}
        />
        <BottomNav onNext={handleNext} />
      </Stack>
    </>
  );
}
